import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Calendar, 
  Clock, 
  User, 
  FileText, 
  LogOut,
  Plus,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: { bg: "bg-yellow-500/20", text: "text-yellow-400", label: "Pending" },
  confirmed: { bg: "bg-green-500/20", text: "text-green-400", label: "Confirmed" },
  cancelled: { bg: "bg-red-500/20", text: "text-red-400", label: "Cancelled" },
  completed: { bg: "bg-blue-500/20", text: "text-blue-400", label: "Completed" },
};

export default function PatientPortal() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders, isAuthenticated, loading } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user?.role === 'admin') {
      navigate('/admin');
      return;
    }

    const fetchAppointments = async () => {
      try {
        const response = await axios.get(`${API}/patient/appointments`, {
          headers: getAuthHeaders()
        });
        setAppointments(response.data);
      } catch (error) {
        console.error("Error fetching appointments:", error);
        toast.error("Failed to load appointments");
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchAppointments();
    }
  }, [isAuthenticated, loading, user, navigate, getAuthHeaders]);

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate('/');
  };

  if (loading || isLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-[#00f0ff] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="patient-portal" className="pt-20 min-h-screen">
      {/* Header */}
      <section className="py-8 border-b border-[#4b0082]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Welcome, <span className="text-gradient">{user?.first_name}</span>
              </h1>
              <p className="text-gray-400 mt-1">Manage your appointments and health information</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/appointment">
                <Button 
                  data-testid="portal-book-appointment"
                  className="bg-[#00f0ff] text-black font-bold rounded-full px-6 hover:bg-[#00c0cc] transition-all"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Book Appointment
                </Button>
              </Link>
              <Button 
                onClick={handleLogout}
                data-testid="portal-logout"
                variant="outline"
                className="border-[#4b0082] text-gray-300 rounded-full hover:border-[#ff00ff] hover:text-[#ff00ff]"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Profile Card */}
            <div className="glass rounded-2xl p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00f0ff] to-[#ff00ff] flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">{user?.first_name} {user?.last_name}</h3>
                  <p className="text-gray-400 text-sm">{user?.email}</p>
                </div>
              </div>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Phone</span>
                  <span className="text-white">{user?.phone}</span>
                </div>
                {user?.date_of_birth && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Date of Birth</span>
                    <span className="text-white">{user?.date_of_birth}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Account Type</span>
                  <span className="text-[#00f0ff] capitalize">{user?.role}</span>
                </div>
              </div>
            </div>

            {/* Appointments */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">Your Appointments</h2>
                <span className="text-gray-400 text-sm">{appointments.length} total</span>
              </div>

              {appointments.length === 0 ? (
                <div className="glass rounded-2xl p-12 text-center">
                  <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-white font-semibold mb-2">No appointments yet</h3>
                  <p className="text-gray-400 mb-6">Schedule your first appointment to get started</p>
                  <Link to="/appointment">
                    <Button className="bg-[#00f0ff] text-black font-bold rounded-full px-6">
                      Book Now
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {appointments.map((apt) => {
                    const status = statusColors[apt.status] || statusColors.pending;
                    return (
                      <div 
                        key={apt.id}
                        data-testid={`appointment-${apt.id}`}
                        className="glass rounded-xl p-5 hover:border-[#00f0ff]/50 transition-colors"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-white font-semibold">{apt.service}</h4>
                              <Badge className={`${status.bg} ${status.text} border-0`}>
                                {status.label}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {apt.preferred_date}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {apt.preferred_time}
                              </span>
                              {apt.doctor && apt.doctor !== "any" && (
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {apt.doctor}
                                </span>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            className="text-[#00f0ff] hover:bg-[#00f0ff]/10"
                          >
                            View Details
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Calendar, label: "Book Appointment", href: "/appointment", color: "#00f0ff" },
              { icon: User, label: "Find a Doctor", href: "/doctors", color: "#ff00ff" },
              { icon: FileText, label: "Our Services", href: "/services", color: "#ff9e00" },
              { icon: Clock, label: "Contact Us", href: "/contact", color: "#00f0ff" },
            ].map((link, index) => (
              <Link 
                key={index} 
                to={link.href}
                className="glass rounded-xl p-4 flex items-center gap-3 hover:border-[#00f0ff]/50 transition-all group"
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${link.color}20` }}
                >
                  <link.icon className="w-5 h-5" style={{ color: link.color }} />
                </div>
                <span className="text-white text-sm font-medium group-hover:text-[#00f0ff] transition-colors">
                  {link.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
